import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import {
  ALL_WESTERN_ZODIACS,
  ZODIAC_EXTRAS,
  getZodiacBySlug,
  zodiacSlug,
} from "@/lib/calendar/zodiac";
import { computePair, pairSlug } from "@/lib/calendar/zodiac-compat";
import { withSlash } from "@/lib/url";

export const Route = createFileRoute("/zodiak/$sign")({
  loader: ({ params }) => {
    const z = getZodiacBySlug(params.sign);
    if (!z) throw notFound();
    return { z };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const z = loaderData.z;
    const title = `${z.nameId} (${z.rangeId}) - Sifat, Elemen & Cocok dengan Siapa`;
    const desc = `Profil lengkap zodiak ${z.nameId}: tanggal lahir ${z.rangeId}, elemen ${z.elementId}, planet ${z.ruler}, sifat khas, dan zodiak yang cocok.`;
    const canonical = `https://hari.co.id${withSlash(`/zodiak/${params.sign}`)}`;
    void ZODIAC_EXTRAS;
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Beranda", item: "https://hari.co.id/" },
          { "@type": "ListItem", position: 2, name: "Zodiak", item: "https://hari.co.id/zodiak/" },
          { "@type": "ListItem", position: 3, name: z.nameId, item: canonical },
        ],
      },
    ];
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(ld) }],
    };
  },
  component: ZodiakSign,
  notFoundComponent: () => (
    <SiteShell>
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold">Zodiak tidak ditemukan</h1>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <SiteShell>
        <div className="py-16 text-center">
          <p className="text-sm text-muted-foreground">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
            Coba lagi
          </button>
        </div>
      </SiteShell>
    );
  },
});

function ZodiakSign() {
  const { z } = Route.useLoaderData();
  const x = ZODIAC_EXTRAS[z.id];
  return (
    <SiteShell>
      <div className="mx-auto max-w-3xl py-8">
        <nav className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Beranda</Link>{" / "}
          <Link to={withSlash("/zodiak") as never} className="hover:text-primary">Zodiak</Link>{" / "}
          {z.nameId}
        </nav>

        <header className="flex items-center gap-4">
          <div className="text-6xl">{z.symbol}</div>
          <div>
            <h1 className="font-display text-4xl tracking-tight md:text-5xl">{z.nameId}</h1>
            <p className="text-muted-foreground">{z.rangeId} · Elemen {z.elementId} · {z.ruler}</p>
          </div>
        </header>

        <p className="mt-6 text-base leading-relaxed">{x?.shortBioId}</p>
        {x?.longBioId && (
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">{x.longBioId}</p>
        )}

        <section className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="font-semibold">Sifat khas</h2>
            <ul className="mt-2 list-inside list-disc text-sm">
              {z.traitsId.map((t: string) => <li key={t}>{t}</li>)}
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="font-semibold">Cocok dengan</h2>
            <div className="mt-2 flex flex-wrap gap-2 text-sm">
              {x?.compatible.map((c) => {
                const target = ALL_WESTERN_ZODIACS.find((zz) => zz.id === c);
                return target ? (
                  <Link key={c} to={withSlash(`/zodiak/${zodiacSlug(target)}`) as never} className="rounded-full border border-border px-3 py-1 hover:bg-accent">
                    {target.nameId}
                  </Link>
                ) : null;
              })}
            </div>
            {x?.challenging && (
              <>
                <h3 className="mt-3 text-sm font-semibold text-muted-foreground">Lebih menantang</h3>
                <div className="mt-1 flex flex-wrap gap-2 text-sm">
                  {x.challenging.map((c) => {
                    const target = ALL_WESTERN_ZODIACS.find((zz) => zz.id === c);
                    return target ? (
                      <Link key={c} to={withSlash(`/zodiak/${zodiacSlug(target)}`) as never} className="rounded-full border border-border px-3 py-1 text-muted-foreground hover:bg-accent">
                        {target.nameId}
                      </Link>
                    ) : null;
                  })}
                </div>
              </>
            )}
          </div>
        </section>

        {x && (
          <section className="mt-6 rounded-lg border border-border bg-card p-4">
            <h2 className="font-semibold">Profil singkat</h2>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
              <div><dt className="text-muted-foreground">Modalitas</dt><dd>{x.modality}</dd></div>
              <div><dt className="text-muted-foreground">Planet</dt><dd>{z.ruler}</dd></div>
              <div><dt className="text-muted-foreground">Bagian tubuh</dt><dd>{x.bodyPart}</dd></div>
              <div><dt className="text-muted-foreground">Warna hoki</dt><dd>{x.luckyColorId}</dd></div>
              <div><dt className="text-muted-foreground">Angka hoki</dt><dd>{x.luckyNumbers.join(", ")}</dd></div>
              <div><dt className="text-muted-foreground">Elemen</dt><dd>{z.elementId}</dd></div>
              {x.luckyDay && (<div><dt className="text-muted-foreground">Hari hoki</dt><dd>{x.luckyDay}</dd></div>)}
            </dl>
          </section>
        )}

        {x && (x.career || x.love || x.health || x.finance) && (
          <section className="mt-6 grid gap-4 sm:grid-cols-2">
            {x.career && (
              <div className="rounded-lg border border-border bg-card p-4">
                <h2 className="font-semibold">Karier {z.nameId}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{x.career}</p>
              </div>
            )}
            {x.love && (
              <div className="rounded-lg border border-border bg-card p-4">
                <h2 className="font-semibold">Asmara {z.nameId}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{x.love}</p>
              </div>
            )}
            {x.health && (
              <div className="rounded-lg border border-border bg-card p-4">
                <h2 className="font-semibold">Kesehatan {z.nameId}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{x.health}</p>
              </div>
            )}
            {x.finance && (
              <div className="rounded-lg border border-border bg-card p-4">
                <h2 className="font-semibold">Keuangan {z.nameId}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{x.finance}</p>
              </div>
            )}
          </section>
        )}

        {x?.famous && x.famous.length > 0 && (
          <section className="mt-6 rounded-lg border border-border bg-card p-4">
            <h2 className="font-semibold">Tokoh Terkenal Berzodiak {z.nameId}</h2>
            <div className="mt-2 flex flex-wrap gap-2 text-sm">
              {x.famous.map((n) => (
                <span key={n} className="rounded-full border border-border px-3 py-1">{n}</span>
              ))}
            </div>
          </section>
        )}

        <section className="mt-8">
          <h2 className="text-xl font-semibold">FAQ {z.nameId}</h2>
          <div className="mt-3 space-y-3 text-sm">
            <div><div className="font-medium">Zodiak {z.nameId} tanggal berapa?</div><div className="text-muted-foreground">{z.nameId} berlaku untuk yang lahir pada {z.rangeId}.</div></div>
            <div><div className="font-medium">{z.nameId} elemen apa?</div><div className="text-muted-foreground">{z.nameId} berelemen {z.elementId} dan dipengaruhi {z.ruler}.</div></div>
            <div><div className="font-medium">{z.nameId} cocok sama siapa?</div><div className="text-muted-foreground">Pasangan paling kompatibel: {x?.compatible.join(", ")}.</div></div>
          </div>
        </section>

        <nav className="mt-10 flex justify-between text-sm">
          {(() => {
            const i = ALL_WESTERN_ZODIACS.findIndex((zz) => zz.id === z.id);
            const prev = ALL_WESTERN_ZODIACS[(i + ALL_WESTERN_ZODIACS.length - 1) % ALL_WESTERN_ZODIACS.length];
            const next = ALL_WESTERN_ZODIACS[(i + 1) % ALL_WESTERN_ZODIACS.length];
            return (
              <>
                <Link to={withSlash(`/zodiak/${zodiacSlug(prev)}`) as never} className="rounded-md border border-border px-3 py-1.5 hover:bg-accent">← {prev.nameId}</Link>
                <Link to={withSlash(`/zodiak/${zodiacSlug(next)}`) as never} className="rounded-md border border-border px-3 py-1.5 hover:bg-accent">{next.nameId} →</Link>
              </>
            );
          })()}
        </nav>

        <section className="mt-10 rounded-lg border border-border bg-card p-5">
          <h2 className="font-display text-xl">Kecocokan {z.nameId} dengan zodiak lain</h2>
          <p className="mt-1 text-sm text-muted-foreground">Skor kecocokan menyeluruh dengan 12 zodiak — klik untuk detail.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 md:grid-cols-3">
            {ALL_WESTERN_ZODIACS.filter((zz) => zz.id !== z.id).map((zz) => {
              const sc = computePair(z, zz).overall;
              const tone = sc >= 80 ? "text-green-600" : sc >= 65 ? "text-amber-600" : "text-red-600";
              return (
                <Link
                  key={zz.id}
                  to={withSlash(`/zodiak/cocok/${pairSlug(z, zz)}`) as never}
                  className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm hover:border-primary"
                >
                  <span className="flex items-center gap-2"><span className="text-lg">{zz.symbol}</span>{zz.nameId}</span>
                  <span className={`font-bold ${tone}`}>{sc}%</span>
                </Link>
              );
            })}
          </div>
          <Link to={withSlash("/zodiak/cocok") as never} className="mt-4 inline-block text-sm font-medium text-primary hover:underline">
            Lihat semua kecocokan →
          </Link>
        </section>
      </div>
    </SiteShell>
  );
}